import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Bell, Mail, MessageSquare, Smartphone, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function NotificationSettings() {
  const navigate = useNavigate();
  const { familyUserId } = useParams();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, [familyUserId]);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notification/preferences/${familyUserId}`);
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notification/preferences/${familyUserId}`, {
        channels: preferences.channels,
        is_emergency_enabled: preferences.is_emergency_enabled,
        is_reminder_enabled: preferences.is_reminder_enabled
      });
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (channel) => {
    const newChannels = preferences.channels.includes(channel)
      ? preferences.channels.filter(c => c !== channel)
      : [...preferences.channels, channel];
    setPreferences({ ...preferences, channels: newChannels });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lädt Einstellungen...</p>
        </div>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-gray-600">Keine Einstellungen gefunden</p>
            <Button onClick={() => navigate(-1)} className="mt-4 w-full">
              Zurück
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Benachrichtigungs-Einstellungen</h1>
          <p className="text-gray-600 mt-2">Verwalten Sie, wie Sie benachrichtigt werden möchten</p>
        </div>

        <div className="space-y-6">
          {/* Notification Types */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Benachrichtigungstypen
              </CardTitle>
              <CardDescription>
                Wählen Sie, für welche Ereignisse Sie benachrichtigt werden möchten
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-red-100 rounded-full">
                    <Bell className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Notfall-Benachrichtigungen</h3>
                    <p className="text-sm text-gray-600">Erhalten Sie sofortige Benachrichtigungen bei Notfällen</p>
                  </div>
                </div>
                <Checkbox
                  checked={preferences.is_emergency_enabled}
                  onCheckedChange={(checked) => 
                    setPreferences({ ...preferences, is_emergency_enabled: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Bell className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Erinnerungs-Benachrichtigungen</h3>
                    <p className="text-sm text-gray-600">Erhalten Sie Benachrichtigungen über Erinnerungen</p>
                  </div>
                </div>
                <Checkbox
                  checked={preferences.is_reminder_enabled}
                  onCheckedChange={(checked) => 
                    setPreferences({ ...preferences, is_reminder_enabled: checked })
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Channels */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                Benachrichtigungskanäle
              </CardTitle>
              <CardDescription>
                Wählen Sie, wie Sie benachrichtigt werden möchten
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <Mail className="w-6 h-6 text-gray-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">E-Mail</h3>
                      <p className="text-sm text-gray-600">Benachrichtigungen per E-Mail erhalten</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={preferences.channels.includes('email')}
                    onCheckedChange={() => toggleChannel('email')}
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <MessageSquare className="w-6 h-6 text-gray-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">SMS</h3>
                      <p className="text-sm text-gray-600">Benachrichtigungen per SMS erhalten</p>
                      <p className="text-xs text-yellow-600 mt-1">⚠️ Phase 6: Noch nicht verfügbar</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={preferences.channels.includes('sms')}
                    onCheckedChange={() => toggleChannel('sms')}
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <Smartphone className="w-6 h-6 text-gray-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Push-Benachrichtigung</h3>
                      <p className="text-sm text-gray-600">Benachrichtigungen in der App erhalten</p>
                      <p className="text-xs text-yellow-600 mt-1">⚠️ Phase 6: Noch nicht verfügbar</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={preferences.channels.includes('push')}
                    onCheckedChange={() => toggleChannel('push')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info Box */}
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <p className="text-sm text-green-800">
                <strong>Phase 7-8 - Robustes Benachrichtigungssystem:</strong>
              </p>
              <ul className="text-sm text-green-700 mt-2 ml-4 list-disc">
                <li><strong>E-Mail:</strong> SendGrid (SENDGRID_API_KEY)</li>
                <li><strong>SMS:</strong> Twilio (TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM_NUMBER)</li>
                <li><strong>Push:</strong> Firebase Cloud Messaging (FCM_SERVER_KEY)</li>
                <li><strong>Zustellungs-Tracking:</strong> Webhooks aktualisieren Status automatisch</li>
                <li><strong>Automatische Wiederholung:</strong> Fehlgeschlagene Benachrichtigungen werden bis zu 3x neu versucht</li>
              </ul>
              <p className="text-xs text-green-600 mt-2">
                Ohne konfigurierte API-Keys werden Benachrichtigungen als "failed" markiert.
              </p>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button 
            onClick={savePreferences} 
            disabled={saving}
            className="w-full"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
          </Button>
        </div>
      </div>
    </div>
  );
}
